import { Collapse, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import PropTypes from "prop-types";
import React from "react";

import { FormatTimeIn12HourFormat } from "../../../global/FormatISODateTime";
import CustomIconButton from "../../../ui library/components/buttons/CustomIconButton";
import Svg, { SvgEnum } from "../../../ui library/components/svgs/standard/svg";
import HNSColors from "../../../ui library/HNSColors";

import useProviderDetailHoursSection from "./hooks/useProviderDetailHoursSection";
import EditHoursCell from "./sections/hours/EditHoursCell";
import ProviderDetailSectionCard from "./sections/shared/ProviderDetailSectionCard";
import ProviderDetailSectionTitle from "./sections/shared/ProviderDetailSectionTitle";
import ProviderDetailVisibilityToggle from "./sections/shared/ProviderDetailVisibilityToggle";

export default function ProviderDetailHoursSection(props) {
  const { permissionState } = props;
  const section = useProviderDetailHoursSection(props);

  if (!permissionState.canViewOfficeHours) {
    return (
      <ProviderDetailSectionCard>
        <Typography className="text-center text-[14px]">Nothing to show</Typography>
      </ProviderDetailSectionCard>
    );
  }

  return (
    <ProviderDetailSectionCard>
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-4">
          <ProviderDetailSectionTitle>Hours Of Operation</ProviderDetailSectionTitle>
          <ProviderDetailVisibilityToggle
            isVisible={section.isSectionVisible}
            onToggle={() => section.setIsSectionVisible((prev) => !prev)}
            sectionName="hours of operation"
            toggleId="toggle-hours-of-operation-visibility"
          />
        </div>

        <Collapse in={section.isSectionVisible} timeout={250} unmountOnExit>
          <div className="h-full min-h-0 overflow-auto [&::-webkit-scrollbar-thumb]:bg-blue-700 [&::-webkit-scrollbar]:[width:6px]">
            <TableContainer>
              <Table sx={{ tableLayout: "fixed" }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: "60%" }}>Day of the Week</TableCell>
                    <TableCell sx={{ width: "35%" }}>Hours of Operation</TableCell>
                    <TableCell sx={{ width: "5%" }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {section.dayRows.map((row, index) => (
                    <TableRow key={row.dayIndex}>
                      {!section.editingDays[index] ? (
                        <>
                          <TableCell>{row.day}</TableCell>
                          <TableCell>
                            {row.timings.length ? row.timings.map((timing, timingIndex) => (
                              <Typography key={timing.id ?? timingIndex}>
                                {`${FormatTimeIn12HourFormat(timing.startTime)} to ${FormatTimeIn12HourFormat(timing.endTime)}`}
                              </Typography>
                            )) : (
                              <div className="w-fit min-w-28 p-2" style={{ borderRadius: "500px", backgroundColor: HNSColors.failure[700] }}>
                                <Typography className="text-center text-[14px]" color={HNSColors.neutrals[100]}>Closed</Typography>
                              </div>
                            )}
                          </TableCell>
                          <TableCell>
                            {permissionState.canEditHoursOfOperation || permissionState.canAddHoursOfOperation ? (
                              <CustomIconButton
                                iconVisible
                                CustomIcon={<Svg name={SvgEnum.HisEdit3} color={HNSColors.primary[500]} onClick={() => section.startEditingDay(index, row.timings)} />}
                              />
                            ) : null}
                          </TableCell>
                        </>
                      ) : (
                        <TableCell colSpan={3} sx={{ px: 0, py: 0 }}>
                          <EditHoursCell
                            canAdd={permissionState.canAddHoursOfOperation}
                            canDelete={permissionState.canDeleteHoursOfOperation}
                            day={row.day}
                            dayIndex={index}
                            hasUnsavedChanges={Boolean(section.hasUnsavedChangesByDay[index])}
                            timings={section.draftHoursByDay[index] || []}
                            onChange={section.updateHourField}
                            onAdd={section.addHourWindow}
                            onDelete={section.removeHourWindow}
                            onSave={section.saveDayHours}
                            onDone={section.stopEditingDay}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </Collapse>
      </div>
    </ProviderDetailSectionCard>
  );
}

ProviderDetailHoursSection.propTypes = {
  permissionState: PropTypes.object.isRequired,
  providerInformation: PropTypes.object.isRequired,
  setProviderInformation: PropTypes.func.isRequired,
};
